import React, { useState, useRef, useEffect } from "react";
import { api, unwrap, errMsg } from "@/lib/api";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sparkles, SendHorizontal, Plus, MessageSquare } from "lucide-react";
import { toast } from "sonner";

export default function AiAssistant() {
  const qc = useQueryClient();
  const [sessionId, setSessionId] = useState(null);
  const [draft, setDraft] = useState("");
  const scrollRef = useRef(null);
  const { data: sessions } = useQuery({ queryKey: ["ai-sessions"], queryFn: async () => unwrap(await api.get("/ai/sessions")) });
  const { data: history } = useQuery({
    queryKey: ["ai-msgs", sessionId],
    queryFn: async () => unwrap(await api.get(`/ai/sessions/${sessionId}/messages`)),
    enabled: !!sessionId,
  });

  const send = useMutation({
    mutationFn: async () => unwrap(await api.post("/ai/chat", { session_id: sessionId, message: draft })),
    onSuccess: (d) => { if (!sessionId) setSessionId(d.session_id); setDraft(""); qc.invalidateQueries({ queryKey: ["ai-msgs", d.session_id] }); qc.invalidateQueries({ queryKey: ["ai-sessions"] }); },
    onError: (e) => toast.error(errMsg(e)),
  });

  useEffect(() => { scrollRef.current?.scrollTo({ top: 999999, behavior: "smooth" }); }, [history]);

  const msgs = history?.messages || [];

  return (
    <div className="p-6 lg:p-8 h-[calc(100vh-4rem)] flex flex-col" data-testid="ai-page">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold text-[var(--text-primary)] flex items-center gap-2"><Sparkles className="h-6 w-6 text-[var(--accent)]" /> Ada — AI tutor</h1>
          <p className="text-sm text-[var(--text-muted)] mt-1">Ask about any subject. Ada guides, doesn't do homework for you.</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => setSessionId(null)} data-testid="new-session-btn"><Plus className="h-4 w-4 mr-1.5" /> New chat</Button>
      </div>
      <div className="flex-1 grid grid-cols-12 gap-4 overflow-hidden">
        <Card className="col-span-3 border-[var(--subtle)] overflow-hidden flex flex-col">
          <CardContent className="p-2 overflow-y-auto">
            <div className="text-xs uppercase tracking-wider text-[var(--text-muted)] px-2 py-2">Recent chats</div>
            {(sessions || []).map((s) => (
              <button key={s.id} onClick={() => setSessionId(s.id)}
                className={`w-full text-left px-3 py-2 rounded text-sm hover:bg-[var(--subtle-bg)] ${sessionId === s.id ? "bg-[var(--accent-bg)] text-[var(--accent)]" : ""}`}
                data-testid={`session-${s.id}`}>
                <MessageSquare className="h-3 w-3 inline mr-1.5" />
                {s.title || "Untitled"}
              </button>
            ))}
            {(sessions || []).length === 0 && <div className="p-3 text-xs text-[var(--text-muted)]">No chats yet. Ask a question below to start.</div>}
          </CardContent>
        </Card>
        <Card className="col-span-9 border-[var(--subtle)] overflow-hidden flex flex-col">
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 space-y-4">
            {msgs.length === 0 && (
              <div className="text-center text-[var(--text-muted)] mt-16">
                <Sparkles className="h-12 w-12 mx-auto opacity-40 mb-3" />
                <div className="text-sm">Ask Ada anything — try "Explain photosynthesis for a class 6 student"</div>
              </div>
            )}
            {msgs.map((m) => (
              <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
                <div className={`max-w-[75%] px-4 py-3 rounded-lg text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-[var(--accent)] text-white" : "bg-[var(--subtle-bg)] text-[var(--text-primary)]"}`}>
                  {m.content}
                </div>
              </div>
            ))}
            {send.isPending && (
              <div className="flex justify-start"><div className="px-4 py-3 bg-[var(--subtle-bg)] rounded-lg text-sm text-[var(--text-muted)]">Ada is thinking…</div></div>
            )}
          </div>
          <div className="border-t border-[var(--subtle)] p-4 flex gap-2">
            <Input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && draft && !send.isPending) send.mutate(); }}
              placeholder="Ask Ada anything..."
              className="flex-1"
              data-testid="ai-input"
              disabled={send.isPending}
            />
            <Button onClick={() => send.mutate()} disabled={!draft || send.isPending} className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white" data-testid="ai-send">
              <SendHorizontal className="h-4 w-4" />
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}
